/**
 * VSG cart gifts: one gift line per parent subscription variant.
 * A gift stays only while that variant is still in the cart as a
 * subscription. Switching the parent back to subscription restores
 * its custom.gift. Recharge cart rows are tagged with .pdp-vsg-gift
 * (matched by product title on [role="listitem"]) so CSS can hide
 * remove controls inside the storefront shadow root.
 */
(function () {
  var PROP_GIFT = '_gift';
  var PROP_PARENT_PRODUCT = '_gift_parent_product_id';
  var PROP_PARENT_VARIANT = '_gift_parent_variant_id';
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var OUR_REQUEST = /(?:\?|&)vsg_gift=1(?:&|$)/;
  var RECHARGE_CART_REQUEST = /(?:\?|&)recharge=true(?:&|$)/;
  var RECHARGE_CART_SELECTORS = [
    'recharge-cart',
    'recharge-sidecart',
    'recharge-cart-drawer',
    'recharge-storefront',
    '[data-recharge-cart]',
    '[data-recharge-sidecart]',
    '[data-recharge-storefront]',
    '#recharge-cart',
    '.recharge-cart'
  ].join(',');
  var RECONCILE_EVENTS = [
    'cartUpdated',
    'cart:updated',
    'cartItemAdded'
  ];

  var GIFT_CLASS = 'pdp-vsg-gift';
  var DUP_CLASS = 'pdp-vsg-gift-duplicate';
  var GIFT_MAP_ID = 'pdp-vsg-gift-map';
  var GIFT_MAP_SECTION = 'cart-drawer';
  var GIFT_SHADOW_CSS =
    '.pdp-vsg-gift button:has(.lucide-trash2),' +
    '.pdp-vsg-gift button:has([class*="lucide-trash"]),' +
    '.pdp-vsg-gift button[aria-label^="Remove"]{display:none!important;}' +
    '.pdp-vsg-gift-duplicate{display:none!important;}';

  var busy = false;
  var pendingReconcile = false;
  var reconcileTimer = null;
  var markTimer = null;
  var markingGifts = false;
  var lastCart = null;
  var sawRechargeCartRequest = false;
  var inFlightGifts = {};

  function itemProperties(item) {
    return (item && item.properties) || {};
  }

  function isGiftItem(item) {
    return String(itemProperties(item)[PROP_GIFT]) === 'true';
  }

  function giftProperties(parentProductId, parentVariantId) {
    var properties = {};
    properties[PROP_GIFT] = 'true';
    if (parentProductId != null) {
      properties[PROP_PARENT_PRODUCT] = String(parentProductId);
    }
    if (parentVariantId != null) {
      properties[PROP_PARENT_VARIANT] = String(parentVariantId);
    }
    return properties;
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartMutationUrl(url) {
    return CART_MUTATION.test(String(url || ''));
  }

  function isOurGiftRequest(url) {
    return OUR_REQUEST.test(String(url || ''));
  }

  function rememberCartSource(url) {
    if (!isCartMutationUrl(url) || isOurGiftRequest(url)) return;
    sawRechargeCartRequest = RECHARGE_CART_REQUEST.test(String(url || ''));
  }

  function hasRechargeCartDom() {
    try {
      return !!document.querySelector(RECHARGE_CART_SELECTORS);
    } catch (err) {
      return false;
    }
  }

  function isRechargeCartActive() {
    return sawRechargeCartRequest || hasRechargeCartDom();
  }

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    }).then(function (response) {
      if (!response.ok) throw new Error('Failed to load cart');
      return response.json();
    });
  }

  function hasGiftVariant(cart, giftVariantId, parentVariantId) {
    if (!cart || !cart.items || giftVariantId == null) return false;
    var id = Number(giftVariantId);
    var parentId = parentVariantId == null ? '' : String(parentVariantId);
    return cart.items.some(function (item) {
      if (!isGiftItem(item) || Number(item.variant_id) !== id) return false;
      if (!parentId) return true;
      return String(itemProperties(item)[PROP_PARENT_VARIANT] || '') === parentId;
    });
  }

  function parseGiftMapJson(text) {
    try {
      var parsed = JSON.parse(String(text || '').trim() || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (err) {
      return {};
    }
  }

  function parseGiftMapFromHtml(html) {
    var match = String(html || '').match(
      /<script[^>]*\bid=["']pdp-vsg-gift-map["'][^>]*>([\s\S]*?)<\/script>/i
    );
    return match ? parseGiftMapJson(match[1]) : {};
  }

  function toGiftId(value) {
    if (value == null || value === '') return null;
    var parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  function readGiftBoxesIntoMap(map) {
    var next = map || {};
    var boxes = document.querySelectorAll('[data-pdp-vsg-gift-box]');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      var parentId = box.getAttribute('data-gift-parent-variant-id');
      var giftVariantId = toGiftId(box.getAttribute('data-gift-variant-id'));
      var giftProductId = toGiftId(box.getAttribute('data-gift-product-id'));
      if (!parentId || giftVariantId == null) continue;
      next[String(parentId)] = {
        productId: giftProductId,
        variantId: giftVariantId,
        available: box.getAttribute('data-gift-available') !== 'false'
      };
    }
    return next;
  }

  function readGiftMapFromDom() {
    var el = document.getElementById(GIFT_MAP_ID);
    var map = el ? parseGiftMapJson(el.textContent) : {};
    return readGiftBoxesIntoMap(map);
  }

  function storeGiftMap(map) {
    var el = document.getElementById(GIFT_MAP_ID);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/json';
      el.id = GIFT_MAP_ID;
      document.body.appendChild(el);
    }
    try {
      el.textContent = JSON.stringify(map || {});
    } catch (err) {}
  }

  function giftMapSectionsUrl() {
    var root = '/';
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      root = window.Shopify.routes.root;
    }
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + '?sections=' + GIFT_MAP_SECTION;
  }

  function hasGiftForParent(cart, parentVariantId) {
    var parentId = String(parentVariantId || '');
    if (!parentId) return false;
    return ((cart && cart.items) || []).some(function (item) {
      if (!isGiftItem(item)) return false;
      return String(itemProperties(item)[PROP_PARENT_VARIANT] || '') === parentId;
    });
  }

  function subscriptionNeedsGiftLookup(cart, map) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!isSubscriptionItem(item) || item.variant_id == null) continue;
      if (hasGiftForParent(cart, item.variant_id)) continue;
      if (!map[String(item.variant_id)]) return true;
    }
    return false;
  }

  function fetchGiftMapFromSection() {
    return fetch(giftMapSectionsUrl(), {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    }).then(function (response) {
      if (!response.ok) throw new Error('Failed to load gift map');
      return response.json();
    }).then(function (data) {
      return parseGiftMapFromHtml(data && data[GIFT_MAP_SECTION]);
    });
  }

  function resolveGiftMap(cart) {
    var map = readGiftMapFromDom();
    if (!subscriptionNeedsGiftLookup(cart, map)) return Promise.resolve(map);
    return fetchGiftMapFromSection()
      .then(function (sectionMap) {
        var merged = Object.assign({}, map, sectionMap || {});
        storeGiftMap(merged);
        return readGiftBoxesIntoMap(merged);
      })
      .catch(function () {
        return map;
      });
  }

  function giftsToAdd(cart, giftMap) {
    var adds = [];
    var seen = {};
    ((cart && cart.items) || []).forEach(function (item) {
      if (!isSubscriptionItem(item) || item.variant_id == null) return;
      var parentVariantId = String(item.variant_id);
      if (seen[parentVariantId]) return;
      seen[parentVariantId] = true;
      var entry = giftMap && giftMap[parentVariantId];
      if (!entry || entry.available === false || entry.variantId == null) return;
      if (hasGiftVariant(cart, entry.variantId, item.variant_id)) return;
      if (inFlightGifts[pendingGiftKey(entry.variantId, item.variant_id)]) return;
      adds.push({
        id: Number(entry.variantId),
        quantity: 1,
        properties: giftProperties(item.product_id, item.variant_id)
      });
    });
    return adds;
  }

  function isSubscriptionItem(item) {
    if (!item || isGiftItem(item)) return false;
    if (item.selling_plan_allocation || item.selling_plan_id) return true;
    var props = itemProperties(item);
    return !!(
      props.selling_plan ||
      props.shipping_interval_frequency ||
      props.shipping_interval_unit_type
    );
  }

  function subscriptionParents(items) {
    var byVariant = {};
    var byProduct = {};
    (items || []).forEach(function (item) {
      if (!isSubscriptionItem(item)) return;
      if (item.variant_id != null) byVariant[String(item.variant_id)] = true;
      if (item.product_id != null) byProduct[String(item.product_id)] = true;
    });
    return { byVariant: byVariant, byProduct: byProduct };
  }

  function giftHasLivingParent(item, parents) {
    var props = itemProperties(item);
    var parentVariantId = String(props[PROP_PARENT_VARIANT] || '');
    var parentProductId = String(props[PROP_PARENT_PRODUCT] || '');
    if (parentVariantId) return !!parents.byVariant[parentVariantId];
    if (parentProductId) return !!parents.byProduct[parentProductId];
    return false;
  }

  function giftIdentity(item) {
    var props = itemProperties(item);
    return (
      String(item.variant_id) +
      ':' +
      String(props[PROP_PARENT_VARIANT] || props[PROP_PARENT_PRODUCT] || '')
    );
  }

  function pendingGiftKey(variantId, parentVariantId) {
    return String(variantId) + ':' + String(parentVariantId || '');
  }

  function giftAddKey(item) {
    var props = (item && item.properties) || {};
    return pendingGiftKey(item && item.id, props[PROP_PARENT_VARIANT]);
  }

  function markGiftsInFlight(items, on) {
    (items || []).forEach(function (item) {
      var key = giftAddKey(item);
      if (!key) return;
      if (on) inFlightGifts[key] = true;
      else delete inFlightGifts[key];
    });
  }

  function updatesForCart(cart) {
    var updates = {};
    var parents = subscriptionParents(cart && cart.items);
    var seen = {};
    ((cart && cart.items) || []).forEach(function (item) {
      if (!isGiftItem(item) || !item.key) return;
      if (!giftHasLivingParent(item, parents) || seen[giftIdentity(item)]) {
        updates[item.key] = 0;
        return;
      }
      seen[giftIdentity(item)] = true;
      if (item.quantity !== 1) updates[item.key] = 1;
    });
    return updates;
  }

  function jsonPost(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) throw new Error('Failed to update cart gift');
      return response.json();
    });
  }

  function addGiftItems(items) {
    if (!items || !items.length) return Promise.resolve(null);
    var url = isRechargeCartActive()
      ? '/cart/add.js?recharge=true&vsg_gift=1'
      : '/cart/add.js?vsg_gift=1';
    markGiftsInFlight(items, true);
    return items
      .reduce(function (promise, item) {
        return promise.then(function () {
          return jsonPost(url, item).catch(function () {
            markGiftsInFlight([item], false);
            return null;
          });
        });
      }, Promise.resolve())
      .then(
        function (result) {
          return result;
        },
        function (err) {
          markGiftsInFlight(items, false);
          throw err;
        }
      );
  }

  function applyRechargeUpdates(updates) {
    return Object.keys(updates).reduce(function (promise, key) {
      return promise.then(function () {
        return jsonPost('/cart/change.js?recharge=true&vsg_gift=1', {
          id: key,
          quantity: updates[key]
        });
      });
    }, Promise.resolve());
  }

  function applyThemeUpdates(updates) {
    return jsonPost('/cart/update.js?vsg_gift=1', { updates: updates });
  }

  function applyUpdates(updates) {
    if (isRechargeCartActive()) return applyRechargeUpdates(updates);
    return applyThemeUpdates(updates);
  }

  function refreshCartUi() {
    if (isRechargeCartActive()) return;
    document.dispatchEvent(new CustomEvent('cartUpdated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  function forEachElement(root, fn) {
    if (!root) return;
    if (root.nodeType === 1) {
      fn(root);
      if (root.shadowRoot) forEachElement(root.shadowRoot, fn);
    }
    var children = root.children;
    if (!children) return;
    for (var i = 0; i < children.length; i++) forEachElement(children[i], fn);
  }

  function normalizeText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function itemTitle(item) {
    return normalizeText(item && (item.product_title || item.title));
  }

  function cartTitleCounts(cart) {
    var counts = {};
    ((cart && cart.items) || []).forEach(function (item) {
      var title = itemTitle(item);
      if (!title) return;
      if (!counts[title]) counts[title] = { gifts: 0, paid: 0 };
      if (isGiftItem(item)) counts[title].gifts += 1;
      else counts[title].paid += 1;
    });
    return counts;
  }

  function giftTitles(cart) {
    var titles = {};
    var counts = cartTitleCounts(cart);
    Object.keys(counts).forEach(function (title) {
      if (counts[title].gifts) titles[title] = counts[title].gifts;
    });
    return titles;
  }

  function listItemTitle(el) {
    if (!el || !el.querySelector) return '';
    var titleEl = el.querySelector('p.font-semibold') || el.querySelector('.font-semibold');
    return normalizeText(titleEl && titleEl.textContent);
  }

  function listItemLooksFree(el) {
    if (!el) return false;
    if (el.querySelector && el.querySelector('[part="money discount"], [part~="discount"]')) return true;
    var text = el.textContent || '';
    return /\$0\.00/.test(text) || /Saving 100%/i.test(text);
  }

  function giftShadowCss() {
    var tpl = document.getElementById('pdp-vsg-gift-shadow-css');
    if (tpl) {
      var css = '';
      if (tpl.content) css = String(tpl.content.textContent || '').trim();
      if (!css) css = String(tpl.innerHTML || tpl.textContent || '').trim();
      if (css) return css;
    }
    return GIFT_SHADOW_CSS;
  }

  function ensureShadowGiftStyle(el) {
    var root = el && el.getRootNode && el.getRootNode();
    if (!root || root === document || !root.host) return;
    var css = giftShadowCss();
    var existing = root.querySelector('style[data-pdp-vsg-gift-style]');
    if (existing) {
      if (css && existing.textContent.indexOf(DUP_CLASS) === -1) existing.textContent = css;
      return;
    }
    var style = document.createElement('style');
    style.setAttribute('data-pdp-vsg-gift-style', '');
    style.textContent = css;
    root.appendChild(style);
  }

  function markGiftNode(el, on) {
    if (!el || !el.classList) return;
    if (on) {
      el.classList.add(GIFT_CLASS);
      el.setAttribute('data-pdp-vsg-gift', '');
      ensureShadowGiftStyle(el);
      return;
    }
    if (!el.hasAttribute('data-pdp-vsg-gift') && !el.classList.contains(GIFT_CLASS)) return;
    el.classList.remove(GIFT_CLASS);
    el.removeAttribute('data-pdp-vsg-gift');
  }

  function markDuplicateNode(el, on) {
    if (!el || !el.classList) return;
    if (on) {
      el.classList.add(DUP_CLASS);
      el.setAttribute('data-pdp-vsg-gift-duplicate', '');
      el.style.setProperty('display', 'none', 'important');
      ensureShadowGiftStyle(el);
      return;
    }
    if (
      !el.hasAttribute('data-pdp-vsg-gift-duplicate') &&
      !el.classList.contains(DUP_CLASS)
    ) {
      return;
    }
    el.classList.remove(DUP_CLASS);
    el.removeAttribute('data-pdp-vsg-gift-duplicate');
    el.style.removeProperty('display');
  }

  function groupGiftTitleRows(root, titles) {
    var grouped = {};
    forEachElement(root, function (el) {
      if (!el.getAttribute || el.getAttribute('role') !== 'listitem') return;
      var title = listItemTitle(el);
      if (!title || !titles[title]) return;
      if (!grouped[title]) grouped[title] = [];
      grouped[title].push(el);
    });
    return grouped;
  }

  function collectGiftRows(grouped, titles) {
    var keep = [];
    Object.keys(grouped).forEach(function (title) {
      var rows = grouped[title];
      var need = titles[title] || 0;
      var free = rows.filter(listItemLooksFree);
      var chosen = (free.length ? free : rows).slice(0, need);
      chosen.forEach(function (el) {
        keep.push(el);
      });
    });
    return keep;
  }

  function collectDuplicateRows(grouped, counts, keep) {
    var hide = [];
    Object.keys(grouped).forEach(function (title) {
      var extras = grouped[title].filter(function (el) {
        return keep.indexOf(el) === -1;
      });
      var paidAllowed = (counts[title] && counts[title].paid) || 0;
      var overflow = extras.length - paidAllowed;
      if (overflow > 0) hide.push.apply(hide, extras.slice(0, overflow));
    });
    return hide;
  }

  function markGiftItems(cart) {
    if (!cart || markingGifts || !document.body) return;
    markingGifts = true;
    var counts = cartTitleCounts(cart);
    var titles = giftTitles(cart);
    var grouped = groupGiftTitleRows(document.body, titles);
    var keep = collectGiftRows(grouped, titles);
    var hide = collectDuplicateRows(grouped, counts, keep);
    try {
      forEachElement(document.body, function (el) {
        var isKeep = keep.indexOf(el) !== -1;
        var isHide = hide.indexOf(el) !== -1;
        var markedGift =
          el.classList &&
          (el.classList.contains(GIFT_CLASS) || el.hasAttribute('data-pdp-vsg-gift'));
        var markedDup =
          el.classList &&
          (el.classList.contains(DUP_CLASS) ||
            el.hasAttribute('data-pdp-vsg-gift-duplicate'));
        if (markedGift && !isKeep) markGiftNode(el, false);
        if (markedDup && !isHide) markDuplicateNode(el, false);
      });
      keep.forEach(function (el) {
        markGiftNode(el, true);
      });
      hide.forEach(function (el) {
        markDuplicateNode(el, true);
      });
    } finally {
      markingGifts = false;
    }
  }

  function scheduleMarkGiftItems() {
    if (markTimer) cancelAnimationFrame(markTimer);
    markTimer = requestAnimationFrame(function () {
      markTimer = null;
      observeShadowRoots();
      if (lastCart) markGiftItems(lastCart);
    });
  }

  function observeShadowRoots() {
    if (!document.body) return;
    forEachElement(document.body, function (el) {
      if (!el.shadowRoot || el.shadowRoot.__pdpVsgGiftObs) return;
      el.shadowRoot.__pdpVsgGiftObs = true;
      var obs = new MutationObserver(function () {
        if (markingGifts) return;
        scheduleMarkGiftItems();
      });
      obs.observe(el.shadowRoot, { childList: true, subtree: true });
    });
  }

  function observeCartHosts() {
    if (document.body && !document.body.__pdpVsgGiftObs) {
      document.body.__pdpVsgGiftObs = true;
      var obs = new MutationObserver(function () {
        if (markingGifts) return;
        scheduleMarkGiftItems();
      });
      obs.observe(document.body, { childList: true, subtree: true });
    }
    observeShadowRoots();
  }

  function reconcile() {
    if (busy) {
      pendingReconcile = true;
      return Promise.resolve();
    }
    busy = true;
    pendingReconcile = false;
    var changed = false;
    return fetchCart()
      .then(function (cart) {
        lastCart = cart;
        markGiftItems(cart);
        observeCartHosts();
        var updates = updatesForCart(cart);
        if (!Object.keys(updates).length) return cart;
        changed = true;
        return applyUpdates(updates).then(function () {
          return fetchCart();
        });
      })
      .then(function (cart) {
        if (cart) lastCart = cart;
        return resolveGiftMap(lastCart).then(function (giftMap) {
          var adds = giftsToAdd(lastCart, giftMap);
          if (!adds.length) return null;
          changed = true;
          return addGiftItems(adds)
            .then(function () {
              return fetchCart();
            })
            .then(
              function (cart) {
                markGiftsInFlight(adds, false);
                return cart;
              },
              function (err) {
                markGiftsInFlight(adds, false);
                throw err;
              }
            );
        });
      })
      .then(function (cart) {
        if (cart) lastCart = cart;
        if (lastCart) markGiftItems(lastCart);
        if (changed) {
          refreshCartUi();
          scheduleMarkGiftItems();
        }
        return null;
      })
      .catch(function () {
        return null;
      })
      .then(function () {
        busy = false;
        if (!pendingReconcile) return null;
        pendingReconcile = false;
        return reconcile();
      });
  }

  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      reconcile();
    }, 200);
  }

  function watchFetch() {
    var originalFetch = window.fetch;
    if (!originalFetch || originalFetch.__pdpVsgGifts) return;
    function wrappedFetch(input) {
      var url = requestUrl(input);
      rememberCartSource(url);
      var shouldWatch = isCartMutationUrl(url) && !isOurGiftRequest(url);
      var promise = originalFetch.apply(this, arguments);
      if (shouldWatch && promise && typeof promise.then === 'function') {
        promise.then(
          function (response) {
            if (response && response.ok) scheduleReconcile();
          },
          function () {}
        );
      }
      return promise;
    }
    wrappedFetch.__pdpVsgGifts = true;
    window.fetch = wrappedFetch;
  }

  function watchXhr() {
    var proto = XMLHttpRequest.prototype;
    if (proto.open.__pdpVsgGifts) return;
    var originalOpen = proto.open;
    var originalSend = proto.send;

    proto.open = function (method, url) {
      this.__pdpVsgUrl = url;
      return originalOpen.apply(this, arguments);
    };
    proto.open.__pdpVsgGifts = true;

    proto.send = function () {
      var xhr = this;
      xhr.addEventListener('load', function () {
        var url = xhr.__pdpVsgUrl;
        rememberCartSource(url);
        if (
          xhr.status >= 200 &&
          xhr.status < 300 &&
          isCartMutationUrl(url) &&
          !isOurGiftRequest(url)
        ) {
          scheduleReconcile();
        }
      });
      return originalSend.apply(this, arguments);
    };
  }

  function isGiftControl(event) {
    var path = event.composedPath ? event.composedPath() : [event.target];
    var inGift = false;
    var isControl = false;
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node || node.nodeType !== 1) continue;
      if (
        (node.classList && node.classList.contains(GIFT_CLASS)) ||
        (node.getAttribute && node.getAttribute('data-pdp-vsg-gift') != null)
      ) {
        inGift = true;
      }
      if (
        node.matches &&
        (node.matches('[aria-label^="Remove"]') ||
          (node.matches('button') &&
            node.querySelector &&
            node.querySelector('.lucide-trash2, [class*="lucide-trash"]')))
      ) {
        isControl = true;
      }
    }
    return inGift && isControl;
  }

  function ensureWatch() {
    watchFetch();
    watchXhr();
  }

  function boot() {
    if (document.documentElement.getAttribute('data-pdp-vsg-cart-gifts') === 'true') return;
    document.documentElement.setAttribute('data-pdp-vsg-cart-gifts', 'true');

    ensureWatch();
    window.addEventListener('load', ensureWatch);
    setTimeout(ensureWatch, 1000);

    document.addEventListener(
      'click',
      function (event) {
        if (!isGiftControl(event)) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    RECONCILE_EVENTS.forEach(function (name) {
      document.addEventListener(name, scheduleReconcile);
    });

    observeCartHosts();
    scheduleReconcile();
  }

  window.PdpVsgCartGifts = {
    PROP_GIFT: PROP_GIFT,
    PROP_PARENT_PRODUCT: PROP_PARENT_PRODUCT,
    PROP_PARENT_VARIANT: PROP_PARENT_VARIANT,
    isGiftItem: isGiftItem,
    giftProperties: giftProperties,
    fetchCart: fetchCart,
    hasGiftVariant: hasGiftVariant,
    isRechargeCartActive: isRechargeCartActive,
    reconcile: reconcile
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
